import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createCustomPage as createCustomPageAPI,
  updateCustomPage as updateCustomPageAPI,
  deleteCustomPage as deleteCustomPageAPI,
  deleteCustomPageSection as deleteCustomPageSectionAPI,
  uploadCustomPageImage,
  removeCustomPageImageContent,
  getCustomPageImageContent,
  getCustomPages,
  createPageSections,
  mapComponentToMediaType,
  updateCustomPageRichMediaContent,
  getCustomPageRichMediaContent,
  updateCustomPageSectionOrder,
  CustomPageVO,
  CustomPageSectionVO,
  UploadedImageVO,
  CustomPageRichMediaContentVO,
} from 'modules/customPages/services/CustomPageService';
import OriginalImageCropParams from 'model/OriginalImageCropParams';
import { convertEncodedStringToBlob } from 'utils/fileUtil';
import { updateBenguideLatestRevision } from 'modules/auth/slices/benguideSlice';
import { getBase64Image } from 'utils/screenshotUtil';

export interface CustomPage {
  id: string;
  title: string;
  path: string;
  components: any[];
  createdAt?: string;
  updatedAt?: string;
}

interface CustomPagesState {
  customPages: CustomPage[];
  inProgress: boolean;
  error: any;
  createInProgress: boolean;
  updateInProgress: boolean;
  deleteInProgress: boolean;
  currentPage: CustomPage | null;
  requestType: string;
  imageUploadInProgress: boolean;
  imageUploadError: any;
  imageContentInProgress: boolean;
  imageContentError: any;
  imageContentUrls: Record<string, string>;
  richMediaUpdateInProgress: boolean;
  richMediaUpdateError: any;
  richMediaFetchInProgress: boolean;
  richMediaFetchError: any;
  richMediaContent: Record<string, { content: string; title: string }>;
}

const initialState = {
  customPages: [],
  inProgress: false,
  error: null,
  createInProgress: false,
  updateInProgress: false,
  deleteInProgress: false,
  currentPage: null,
  requestType: '',
  imageUploadInProgress: false,
  imageUploadError: null,
  imageContentInProgress: false,
  imageContentError: null,
  imageContentUrls: {},
  richMediaUpdateInProgress: false,
  richMediaUpdateError: null,
  richMediaFetchInProgress: false,
  richMediaFetchError: null,
  richMediaContent: {},
} as CustomPagesState;

const customPagesSlice = createSlice({
  name: 'customPages',
  initialState,
  reducers: {
    // Fetch custom pages
    fetchCustomPagesStarted: (state) => {
      state.inProgress = true;
      state.error = null;
      state.requestType = 'fetchCustomPages';
    },
    fetchCustomPagesSuccess: (state, action: PayloadAction<CustomPage[]>) => {
      state.customPages = action.payload;
      state.inProgress = false;
      state.requestType = 'fetchCustomPages';
    },
    fetchCustomPagesFailed: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.inProgress = false;
      state.requestType = 'fetchCustomPages';
    },

    // Create custom page
    createCustomPageStarted: (state) => {
      state.createInProgress = true;
      state.error = null;
      state.requestType = 'createCustomPage';
    },
    createCustomPageSuccess: (state, action: PayloadAction<CustomPage>) => {
      state.customPages.push(action.payload);
      state.createInProgress = false;
      state.requestType = 'createCustomPage';
    },
    createCustomPageFailed: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.createInProgress = false;
      state.requestType = 'createCustomPage';
    },

    // Update custom page
    updateCustomPageStarted: (state) => {
      state.updateInProgress = true;
      state.error = null;
      state.requestType = 'updateCustomPage';
    },
    updateCustomPageSuccess: (
      state,
      action: PayloadAction<{ pageId: string; updates: Partial<CustomPage> }>
    ) => {
      const { pageId, updates } = action.payload;
      const index = state.customPages.findIndex((page) => page.id === pageId);
      if (index !== -1) {
        state.customPages[index] = { ...state.customPages[index], ...updates };
      }
      state.updateInProgress = false;
      state.requestType = 'updateCustomPage';
    },
    updateCustomPageFailed: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.updateInProgress = false;
      state.requestType = 'updateCustomPage';
    },

    // Delete custom page
    deleteCustomPageStarted: (state) => {
      state.deleteInProgress = true;
      state.error = null;
      state.requestType = 'deleteCustomPage';
    },
    deleteCustomPageSuccess: (state, action: PayloadAction<string>) => {
      state.customPages = state.customPages.filter(
        (page) => page.id !== action.payload
      );
      state.deleteInProgress = false;
      state.requestType = 'deleteCustomPage';
    },
    deleteCustomPageFailed: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.deleteInProgress = false;
      state.requestType = 'deleteCustomPage';
    },

    // Set current page
    setCurrentPage: (state, action: PayloadAction<CustomPage | null>) => {
      state.currentPage = action.payload;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Image upload actions
    uploadCustomPageImageStarted: (state) => {
      state.imageUploadInProgress = true;
      state.imageUploadError = null;
    },
    uploadCustomPageImageSuccess: (state) => {
      state.imageUploadInProgress = false;
      state.imageUploadError = null;
    },
    uploadCustomPageImageFailed: (state, action: PayloadAction<any>) => {
      state.imageUploadInProgress = false;
      state.imageUploadError = action.payload;
    },

    // Image removal actions
    removeCustomPageImageStarted: (
      state,
      action: PayloadAction<{ pageId: string; componentId: string }>
    ) => {
      state.imageUploadInProgress = true;
      state.imageUploadError = null;
      state.requestType = 'removeCustomPageImage';

      const { pageId, componentId } = action.payload;
      const pageIndex = state.customPages.findIndex(
        (page) => page.id === pageId
      );
      if (pageIndex !== -1) {
        state.customPages[pageIndex].components = state.customPages[
          pageIndex
        ].components.filter((comp) => comp.id !== componentId);
      }
    },
    removeCustomPageImageSuccess: (state) => {
      state.imageUploadInProgress = false;
      state.imageUploadError = null;
      state.requestType = 'removeCustomPageImage';
    },
    removeCustomPageImageFailed: (state, action: PayloadAction<any>) => {
      state.imageUploadInProgress = false;
      state.imageUploadError = action.payload;
      state.requestType = 'removeCustomPageImage';
    },

    // Delete custom page section actions
    deleteCustomPageSectionStarted: (
      state,
      action: PayloadAction<{ pageId: string; componentId: string }>
    ) => {
      state.deleteInProgress = true;
      state.error = null;
      state.requestType = 'deleteCustomPageSection';

      const { pageId, componentId } = action.payload;
      const pageIndex = state.customPages.findIndex(
        (page) => page.id === pageId
      );
      if (pageIndex !== -1) {
        state.customPages[pageIndex].components = state.customPages[
          pageIndex
        ].components.filter((comp) => comp.id !== componentId);
      }
    },
    deleteCustomPageSectionSuccess: (state) => {
      state.deleteInProgress = false;
      state.error = null;
      state.requestType = 'deleteCustomPageSection';
    },
    deleteCustomPageSectionFailed: (state, action: PayloadAction<any>) => {
      state.deleteInProgress = false;
      state.error = action.payload;
      state.requestType = 'deleteCustomPageSection';
    },

    // Image content fetching actions
    getCustomPageImageContentStarted: (state) => {
      state.imageContentInProgress = true;
      state.imageContentError = null;
      state.requestType = 'getCustomPageImageContent';
    },
    getCustomPageImageContentSuccess: (
      state,
      action: PayloadAction<{ key: string; url: string }>
    ) => {
      state.imageContentInProgress = false;
      state.imageContentError = null;
      state.requestType = 'getCustomPageImageContent';
      state.imageContentUrls[action.payload.key] = action.payload.url;
    },
    getCustomPageImageContentFailed: (state, action: PayloadAction<any>) => {
      state.imageContentInProgress = false;
      state.imageContentError = action.payload;
      state.requestType = 'getCustomPageImageContent';
    },

    // Update image reference in component after upload
    updateComponentImageReference: (
      state,
      action: PayloadAction<{
        customPageId: string;
        componentId: string;
        imageReference: string;
      }>
    ) => {
      const { customPageId, componentId, imageReference } = action.payload;
      const page = state.customPages.find((page) => page.id === customPageId);
      if (page) {
        const component = page.components.find(
          (comp) => comp.id === componentId
        );
        if (component && component.type === 'image') {
          component.imageReference = imageReference;
        }
      }
    },

    // Rich media content update reducers
    richMediaUpdateStarted: (state) => {
      state.richMediaUpdateInProgress = true;
      state.richMediaUpdateError = null;
      state.requestType = 'richMediaUpdate';
    },

    richMediaUpdateSuccess: (
      state,
      action: PayloadAction<{
        customPageId: string;
        componentId: string;
        content: string;
        title?: string;
      }>
    ) => {
      state.richMediaUpdateInProgress = false;
      state.richMediaUpdateError = null;
      const { customPageId, componentId, content, title } = action.payload;

      const page = state.customPages.find((page) => page.id === customPageId);
      if (page) {
        const component = page.components.find(
          (comp) => comp.id === componentId
        );
        if (component) {
          if (component.type === 'rich_media') {
            component.content = content;
          } else if (component.type === 'content_card') {
            component.content = content;
            if (title !== undefined) {
              component.title = title;
            }
          }
        }
      }
    },

    richMediaUpdateFailed: (state, action: PayloadAction<any>) => {
      state.richMediaUpdateInProgress = false;
      state.richMediaUpdateError = action.payload;
      state.requestType = 'richMediaUpdate';
    },

    richMediaUpdateCompleted: (state) => {
      state.richMediaUpdateInProgress = false;
      state.richMediaUpdateError = null;
      state.requestType = 'richMediaUpdate';
    },

    // Rich media content fetch reducers
    richMediaFetchStarted: (state) => {
      state.richMediaFetchInProgress = true;
      state.richMediaFetchError = null;
      state.requestType = 'richMediaFetch';
    },

    richMediaFetchSuccess: (
      state,
      action: PayloadAction<{
        customPageId: string;
        componentId: string;
        content: string;
        title: string;
      }>
    ) => {
      state.richMediaFetchInProgress = false;
      state.richMediaFetchError = null;
      state.requestType = 'richMediaFetch';

      const { customPageId, componentId, content, title } = action.payload;
      const key = `${customPageId}-${componentId}`;
      state.richMediaContent[key] = { content, title };

      // Also update the component in the customPages array
      const page = state.customPages.find((p) => p.id === customPageId);
      if (page) {
        const component = page.components.find((c) => c.id === componentId);
        if (component) {
          component.content = content;
          if (title !== undefined) {
            component.title = title;
          }
        }
      }
    },

    richMediaFetchFailed: (state, action: PayloadAction<any>) => {
      state.richMediaFetchInProgress = false;
      state.richMediaFetchError = action.payload;
      state.requestType = 'richMediaFetch';
    },

    // Move section reducers (local state only - for immediate UI feedback)
    moveSectionUp: (
      state,
      action: PayloadAction<{ pageId: string; sectionIndex: number }>
    ) => {
      const { pageId, sectionIndex } = action.payload;
      const page = state.customPages.find((p) => p.id === pageId);

      if (page && sectionIndex > 0 && sectionIndex < page.components.length) {
        // Swap the section with the one above it
        const temp = page.components[sectionIndex];
        page.components[sectionIndex] = page.components[sectionIndex - 1];
        page.components[sectionIndex - 1] = temp;

        // Update orderNo values to reflect new positions
        page.components[sectionIndex - 1].orderNo = sectionIndex;
        page.components[sectionIndex].orderNo = sectionIndex + 1;
      }
    },

    moveSectionDown: (
      state,
      action: PayloadAction<{ pageId: string; sectionIndex: number }>
    ) => {
      const { pageId, sectionIndex } = action.payload;
      const page = state.customPages.find((p) => p.id === pageId);

      if (
        page &&
        sectionIndex >= 0 &&
        sectionIndex < page.components.length - 1
      ) {
        // Swap the section with the one below it
        const temp = page.components[sectionIndex];
        page.components[sectionIndex] = page.components[sectionIndex + 1];
        page.components[sectionIndex + 1] = temp;

        // Update orderNo values to reflect new positions
        page.components[sectionIndex].orderNo = sectionIndex + 1;
        page.components[sectionIndex + 1].orderNo = sectionIndex + 2;
      }
    },

    // Section order update API actions
    updateSectionOrderStarted: (state) => {
      state.inProgress = true;
      state.error = null;
      state.requestType = 'updateSectionOrder';
    },

    updateSectionOrderSuccess: (state) => {
      state.inProgress = false;
      state.requestType = 'updateSectionOrder';
    },

    updateSectionOrderFailed: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.inProgress = false;
      state.requestType = 'updateSectionOrder';
    },
  },
});

export const {
  fetchCustomPagesStarted,
  fetchCustomPagesSuccess,
  fetchCustomPagesFailed,
  createCustomPageStarted,
  createCustomPageSuccess,
  createCustomPageFailed,
  updateCustomPageStarted,
  updateCustomPageSuccess,
  updateCustomPageFailed,
  deleteCustomPageStarted,
  deleteCustomPageSuccess,
  deleteCustomPageFailed,
  uploadCustomPageImageStarted,
  uploadCustomPageImageSuccess,
  uploadCustomPageImageFailed,
  removeCustomPageImageStarted,
  removeCustomPageImageSuccess,
  removeCustomPageImageFailed,
  getCustomPageImageContentStarted,
  getCustomPageImageContentSuccess,
  getCustomPageImageContentFailed,
  updateComponentImageReference,
  richMediaUpdateStarted,
  richMediaUpdateSuccess,
  richMediaUpdateFailed,
  richMediaUpdateCompleted,
  richMediaFetchStarted,
  richMediaFetchSuccess,
  richMediaFetchFailed,
  deleteCustomPageSectionStarted,
  deleteCustomPageSectionSuccess,
  deleteCustomPageSectionFailed,
  moveSectionUp,
  moveSectionDown,
  updateSectionOrderStarted,
  updateSectionOrderSuccess,
  updateSectionOrderFailed,
} = customPagesSlice.actions;

// Helper function to get image URL from Redux state (following home page pattern)
export const getImageUrlFromState = (
  state: any,
  customPageId: string,
  customPageSectionId: string,
  original: boolean = false,
  revision?: number
): string => {
  const key = `${customPageId}-${customPageSectionId}-${
    original ? 'original' : 'cropped'
  }-${revision || 'no-revision'}`;

  return state.customPages.imageContentUrls[key] || '';
};

// Helper function to check if image URL is cached in Redux state
export const isImageUrlCached = (
  state: any,
  customPageId: string,
  customPageSectionId: string,
  original: boolean = false,
  revision?: number
): boolean => {
  const key = `${customPageId}-${customPageSectionId}-${
    original ? 'original' : 'cropped'
  }-${revision || 'no-revision'}`;

  return !!state.customPages.imageContentUrls[key];
};

export default customPagesSlice.reducer;

// Generate a URL-friendly path from title
const generatePath = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim();
};

// Helper function to preserve existing image data when setting custom pages from benguide data
const preserveExistingImageData = (
  customPagesData: CustomPageVO[] | null | undefined,
  existingPages: CustomPage[] = []
): CustomPage[] => {
  return (
    customPagesData?.map((apiPage: CustomPageVO) => {
      // Find existing page to preserve image data
      const existingPage = existingPages.find(
        (page: CustomPage) => page.id === apiPage.id
      );

      return {
        id: apiPage.id!,
        title: apiPage.name,
        path: generatePath(apiPage.name),
        components:
          apiPage.pageSections && apiPage.pageSections.length > 0
            ? apiPage.pageSections.map((section) => {
                // Find existing component to preserve image data
                const existingComponent = existingPage?.components?.find(
                  (comp: any) => comp.id === section.id
                );

                return {
                  id: section.id,
                  type: section.mediaType.toLowerCase(),
                  orderNo: section.orderNo,
                  // Preserve existing image data if available, otherwise initialize empty
                  ...(section.mediaType === 'IMAGE' && {
                    imageReference: section.referenceData?.imageReference || '',
                    originalImageReference:
                      section.referenceData?.originalImageReference || '',
                    imageUrl: existingComponent?.imageUrl || '',
                    originalImage: existingComponent?.originalImage || '',
                    cropArea:
                      section.referenceData?.originalImageCropParams ||
                      existingComponent?.cropArea ||
                      null,
                  }),
                  ...(section.mediaType === 'RICH_MEDIA' && {
                    content: section.content || '',
                  }),
                  ...(section.mediaType === 'CONTENT_CARD' && {
                    title: section.title || '',
                    content: section.content || '',
                  }),
                };
              })
            : [],
        createdAt: existingPage?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    }) || []
  );
};

export const setCustomPagesFromBenguideData = (
  customPagesData: CustomPageVO[] | null | undefined
): any => {
  try {
    // For initial load (when no existing pages), just create pages normally
    const pages: CustomPage[] =
      customPagesData?.map((apiPage: CustomPageVO) => ({
        id: apiPage.id!,
        title: apiPage.name,
        path: generatePath(apiPage.name),
        components:
          apiPage.pageSections && apiPage.pageSections.length > 0
            ? apiPage.pageSections.map((section) => ({
                id: section.id,
                type: section.mediaType.toLowerCase(),
                orderNo: section.orderNo,
                // Include content based on component type
                ...(section.mediaType === 'IMAGE' && {
                  imageReference: section.referenceData?.imageReference || '',
                  originalImageReference:
                    section.referenceData?.originalImageReference || '',
                  imageUrl: '',
                  originalImage: '',
                  cropArea:
                    section.referenceData?.originalImageCropParams || null,
                }),
                ...(section.mediaType === 'RICH_MEDIA' && {
                  content: section.content || '',
                }),
                ...(section.mediaType === 'CONTENT_CARD' && {
                  title: section.title || '',
                  content: section.content || '',
                }),
              }))
            : [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })) || [];

    return fetchCustomPagesSuccess(pages);
  } catch (error) {
    console.error('Error in setCustomPagesFromBenguideData:', error);
    return fetchCustomPagesFailed(error);
  }
};

export const setCustomPagesFromBenguideDataWithPreservation = (
  customPagesData: CustomPageVO[] | null | undefined
) => {
  return (dispatch: any, getState: any) => {
    try {
      const state = getState();
      const existingPages = state.customPages?.customPages || [];

      const pages = preserveExistingImageData(customPagesData, existingPages);
      dispatch(fetchCustomPagesSuccess(pages));
    } catch (error) {
      dispatch(fetchCustomPagesFailed(error));
    }
  };
};

export const createCustomPage = async (
  benguideId: string,
  pageTitle: string,
  selectedComponents: any[],
  dispatch: any
): Promise<CustomPage> => {
  dispatch(createCustomPageStarted());
  try {
    // Create page sections from selected components
    const pageSections = createPageSections(selectedComponents);

    const customPageData: CustomPageVO = {
      name: pageTitle,
      pageSections,
    };

    const createdPage = await createCustomPageAPI(benguideId, customPageData);

    // Transform backend response to UI components using backend-generated IDs
    const backendComponents =
      createdPage.pageSections?.map((section) => ({
        id: section.id!,
        type: section.mediaType.toLowerCase(),
        orderNo: section.orderNo,
        // Initialize empty content based on component type
        ...(section.mediaType === 'IMAGE' && {
          imageReference: section.imageReference || '',
          imageUrl: '',
          originalImage: '',
          cropArea: null,
        }),
        ...(section.mediaType === 'RICH_MEDIA' && {
          content: section.content || '',
        }),
        ...(section.mediaType === 'CONTENT_CARD' && {
          title: section.title || '',
          content: section.content || '',
        }),
      })) || [];

    const newPage: CustomPage = {
      id: createdPage.id!,
      title: createdPage.name,
      path: generatePath(createdPage.name),
      components: backendComponents,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    dispatch(createCustomPageSuccess(newPage));
    return newPage;
  } catch (error) {
    dispatch(createCustomPageFailed(error));
    throw error;
  }
};

export const updateCustomPage = async (
  benguideId: string,
  pageId: string,
  updates: Partial<CustomPage>,
  dispatch: any,
  getState: any,
  validateNameChange: boolean = false,
  updateSections: boolean = true
): Promise<void> => {
  dispatch(updateCustomPageStarted());
  try {
    // Get the current page to build the complete CustomPageVO
    const state = getState();
    const currentPage = state.customPages.customPages.find(
      (page: CustomPage) => page.id === pageId
    );

    // Build complete CustomPageVO for the API
    const customPageData: CustomPageVO = {
      id: pageId,
      name: updates.title || currentPage.title,
      pageSections:
        updates.components?.map((comp, index) => ({
          id: comp.id || `comp-${Date.now()}-${index}`,
          mediaType: comp.type.toUpperCase() as
            | 'RICH_MEDIA'
            | 'IMAGE'
            | 'CONTENT_CARD',
          orderNo: comp.orderNo || index + 1,
        })) ||
        currentPage.components?.map((comp: any, index: any) => ({
          id: comp.id || `comp-${Date.now()}-${index}`,
          mediaType: comp.type.toUpperCase() as
            | 'RICH_MEDIA'
            | 'IMAGE'
            | 'CONTENT_CARD',
          orderNo: comp.orderNo || index + 1,
        })) ||
        [],
    };

    const updatedPageVO = await updateCustomPageAPI(
      benguideId,
      pageId,
      customPageData,
      validateNameChange,
      updateSections
    );

    // Transform the response back to our CustomPage format
    const transformedUpdates: Partial<CustomPage> = {
      title: updatedPageVO.name,
      components:
        updatedPageVO.pageSections?.map((section) => ({
          id: section.id!,
          type: section.mediaType.toLowerCase(),
          orderNo: section.orderNo,
        })) || [],
      updatedAt: new Date().toISOString(),
    };

    dispatch(updateCustomPageSuccess({ pageId, updates: transformedUpdates }));
  } catch (error) {
    dispatch(updateCustomPageFailed(error));
    throw error;
  }
};

export const deleteCustomPage = async (
  benguideId: string,
  pageId: string,
  dispatch: any
): Promise<void> => {
  dispatch(deleteCustomPageStarted());
  try {
    await deleteCustomPageAPI(benguideId, pageId);
    dispatch(deleteCustomPageSuccess(pageId));
  } catch (error) {
    dispatch(deleteCustomPageFailed(error));
    throw error;
  }
};

export const refreshCustomPagesData = async (
  benguideId: string,
  dispatch: any,
  getState: any
): Promise<void> => {
  console.log(getState);
  try {
    const response = await getCustomPages(benguideId);
    if (response.customPages) {
      const preservationThunk = setCustomPagesFromBenguideDataWithPreservation(
        response.customPages
      );
      dispatch(preservationThunk);
    }
  } catch (error) {
    throw error;
  }
};

export const refreshCustomPagesDataAction =
  (benguideId: string) =>
  async (dispatch: any): Promise<void> => {
    try {
      const response = await getCustomPages(benguideId);
      if (response.customPages) {
        const preservationThunk =
          setCustomPagesFromBenguideDataWithPreservation(response.customPages);
        dispatch(preservationThunk);
      }
    } catch (error) {
      console.error('Error refreshing custom pages data:', error);
      throw error;
    }
  };

// New action for adding components to an existing page
export const addComponentsToPage = async (
  benguideId: string,
  pageId: string,
  selectedComponents: any[],
  dispatch: any,
  getState: any
): Promise<void> => {
  dispatch(updateCustomPageStarted());
  try {
    // Get the current page from state
    const state = getState();
    const currentPage = state.customPages.customPages.find(
      (page: CustomPage) => page.id === pageId
    );

    // Calculate media-type specific order numbers for new components
    const newPageSections = calculateMediaTypeOrderNumbers(
      selectedComponents,
      currentPage.components
    );

    // When updateSections=true, backend expects ONLY the new sections with proper orderNo
    const customPageData: CustomPageVO = {
      id: pageId,
      name: currentPage.title,
      pageSections: newPageSections,
    };

    const updatedPageVO = await updateCustomPageAPI(
      benguideId,
      pageId,
      customPageData,
      false,
      true
    );

    const backendComponents =
      updatedPageVO.pageSections?.map((section) => {
        const existingComponent = currentPage.components.find(
          (comp: any) => comp.id === section.id
        );

        // For existing components, preserve their cached image data
        const isNewComponent = !existingComponent;

        return {
          id: section.id!,
          type: section.mediaType.toLowerCase(),
          orderNo: section.orderNo,
          ...(section.mediaType === 'IMAGE' && {
            imageReference: section.referenceData?.imageReference || '',
            originalImageReference:
              section.referenceData?.originalImageReference || '',
            imageUrl: isNewComponent ? '' : existingComponent?.imageUrl || '',
            originalImage: isNewComponent
              ? ''
              : existingComponent?.originalImage || '',
            cropArea: isNewComponent
              ? null
              : section.referenceData?.originalImageCropParams ||
                existingComponent?.cropArea ||
                null,
          }),
          ...(section.mediaType === 'RICH_MEDIA' && {
            content: section.content || existingComponent?.content || '',
          }),
          ...(section.mediaType === 'CONTENT_CARD' && {
            title: section.title || existingComponent?.title || '',
            content: section.content || existingComponent?.content || '',
          }),
        };
      }) || [];

    // Transform the response back to our CustomPage format
    const transformedUpdates: Partial<CustomPage> = {
      components: backendComponents, // Use backend components with preserved image data
      updatedAt: new Date().toISOString(),
    };

    dispatch(updateCustomPageSuccess({ pageId, updates: transformedUpdates }));
    const newImageComponents = backendComponents.filter(
      (comp: any) =>
        comp.type === 'image' && comp.imageReference && !comp.imageUrl
    );

    if (newImageComponents.length > 0) {
      // Get the latest state for revision info
      const currentState = getState();
      const revision =
        currentState.benguide?.latestRevision ||
        currentState.benguide?.revision;

      // Fetch image content for new components in the background
      newImageComponents.forEach((comp: any) => {
        dispatch(
          handleGetCustomPageImageContent(
            benguideId,
            pageId,
            comp.id,
            false,
            revision
          )
        );

        // Also fetch original image if it exists
        if (comp.imageReference) {
          dispatch(
            handleGetCustomPageImageContent(
              benguideId,
              pageId,
              comp.id,
              true,
              revision
            )
          );
        }
      });
    }
  } catch (error) {
    dispatch(updateCustomPageFailed(error));
    throw error;
  }
};

// Helper function to check if title exists
export const checkTitleExists = (
  title: string,
  currentPages: CustomPage[]
): boolean => {
  return currentPages.some(
    (page) => page.title.toLowerCase() === title.toLowerCase()
  );
};

// Helper function to calculate sequential order numbers for new components
const calculateMediaTypeOrderNumbers = (
  selectedComponents: any[],
  existingComponents: any[]
): CustomPageSectionVO[] => {
  if (!selectedComponents || !Array.isArray(selectedComponents)) {
    return [];
  }

  // Calculate max orderNo from all existing components (sequential ordering)
  let maxOrderNo = 0;

  if (existingComponents && Array.isArray(existingComponents)) {
    existingComponents.forEach((comp) => {
      const orderNo = comp.orderNo || 0;
      maxOrderNo = Math.max(maxOrderNo, orderNo);
    });
  }

  const sections: CustomPageSectionVO[] = [];

  selectedComponents.forEach((component) => {
    const mediaType = mapComponentToMediaType(component.id);
    const count = Number(component.count) || 0;

    // Increment sequentially for each new component regardless of media type
    for (let i = 0; i < count; i++) {
      sections.push({
        mediaType,
        orderNo: ++maxOrderNo, // Sequential increment for each new component
      });
    }
  });

  return sections;
};

// Custom page image upload action
export const handleCustomPageImageUpload =
  (
    benguideId: string,
    image: string,
    customPageId: string,
    customPageSectionId: string,
    originalImage?: string,
    cropArea?: OriginalImageCropParams,
    originalRef?: string
  ) =>
  async (dispatch: any, getState: any): Promise<UploadedImageVO> => {
    dispatch(uploadCustomPageImageStarted());
    try {
      let updateOriginalRef = originalRef;

      // Upload original image first if provided
      if (originalImage) {
        const originalImgBlob = await convertEncodedStringToBlob(originalImage);
        const originalImgRes = await uploadCustomPageImage(
          benguideId,
          originalImgBlob,
          customPageId,
          customPageSectionId,
          true
        );
        updateOriginalRef = originalImgRes.imageReference;
      }

      // Upload cropped image
      const imageBlob = await convertEncodedStringToBlob(image);
      const response = await uploadCustomPageImage(
        benguideId,
        imageBlob,
        customPageId,
        customPageSectionId,
        false,
        updateOriginalRef,
        cropArea
      );

      // Get current state to preserve existing image cache entries
      const currentState = getState();
      const oldRevision =
        currentState.benguide?.latestRevision ||
        currentState.benguide?.revision;
      const newRevision = response.latestRevision;

      // Update the latest revision for cache busting
      if (response.latestRevision) {
        dispatch(updateBenguideLatestRevision(response.latestRevision));
      }

      // Update the image reference in Redux state so it persists when navigating
      dispatch(
        updateComponentImageReference({
          customPageId,
          componentId: customPageSectionId,
          imageReference: response.imageReference,
        })
      );

      // Cache the image URLs directly in Redux state with new revision
      const croppedKey = `${customPageId}-${customPageSectionId}-cropped-${newRevision}`;
      const originalKey = `${customPageId}-${customPageSectionId}-original-${newRevision}`;

      dispatch(
        getCustomPageImageContentSuccess({
          key: croppedKey,
          url: image,
        })
      );

      if (originalImage) {
        dispatch(
          getCustomPageImageContentSuccess({
            key: originalKey,
            url: originalImage,
          })
        );
      }

      // Preserve existing image cache entries by copying them with the new revision
      // This prevents other image components from losing their cached URLs
      if (oldRevision && newRevision && oldRevision !== newRevision) {
        const existingImageUrls = currentState.customPages.imageContentUrls;
        const updatedImageUrls: Record<string, string> = {};

        Object.keys(existingImageUrls).forEach((key) => {
          // Only copy keys that belong to the same custom page and have the old revision
          if (
            key.includes(`${customPageId}-`) &&
            key.endsWith(`-${oldRevision}`)
          ) {
            // Skip the current component since we just updated it
            if (!key.includes(`${customPageId}-${customPageSectionId}-`)) {
              const newKey = key.replace(`-${oldRevision}`, `-${newRevision}`);
              updatedImageUrls[newKey] = existingImageUrls[key];
            }
          }
        });

        // Batch update all image URLs at once to prevent race conditions
        if (Object.keys(updatedImageUrls).length > 0) {
          Object.entries(updatedImageUrls).forEach(([key, url]) => {
            dispatch(
              getCustomPageImageContentSuccess({
                key,
                url,
              })
            );
          });
        }
      }

      dispatch(uploadCustomPageImageSuccess());
      return response;
    } catch (error) {
      dispatch(uploadCustomPageImageFailed(error));
      throw error;
    }
  };

export const handleCustomPageImageRemoval =
  (benguideId: string, customPageId: string, customPageSectionId: string) =>
  async (dispatch: any, getState: any): Promise<{ latestRevision: number }> => {
    dispatch(
      removeCustomPageImageStarted({
        pageId: customPageId,
        componentId: customPageSectionId,
      })
    );
    try {
      const response = await removeCustomPageImageContent(
        benguideId,
        customPageId,
        customPageSectionId
      );

      // Refresh custom pages data from backend (following home page pattern)
      await refreshCustomPagesData(benguideId, dispatch, getState);
      dispatch(removeCustomPageImageSuccess());
      return response;
    } catch (error) {
      dispatch(removeCustomPageImageFailed(error));
      throw error;
    }
  };

// Custom page section deletion action
export const handleCustomPageSectionDeletion =
  (benguideId: string, customPageId: string, customPageSectionId: string) =>
  async (dispatch: any, getState: any): Promise<{ latestRevision: number }> => {
    dispatch(
      deleteCustomPageSectionStarted({
        pageId: customPageId,
        componentId: customPageSectionId,
      })
    );
    try {
      const response = await deleteCustomPageSectionAPI(
        benguideId,
        customPageId,
        customPageSectionId
      );

      await refreshCustomPagesData(benguideId, dispatch, getState);
      dispatch(deleteCustomPageSectionSuccess());
      return response;
    } catch (error) {
      dispatch(deleteCustomPageSectionFailed(error));
      throw error;
    }
  };

// Get custom page image content action
export const handleGetCustomPageImageContent =
  (
    benguideId: string,
    customPageId: string,
    customPageSectionId: string,
    original: boolean = false,
    revision?: number
  ) =>
  async (dispatch: any, getState: any): Promise<string> => {
    dispatch(getCustomPageImageContentStarted());
    try {
      // Check if already cached first (following home page pattern)
      const state = getState();
      const key = `${customPageId}-${customPageSectionId}-${
        original ? 'original' : 'cropped'
      }-${revision || 'no-revision'}`;

      const cachedUrl = state.customPages.imageContentUrls[key];
      if (cachedUrl) {
        dispatch(getCustomPageImageContentSuccess({ key, url: cachedUrl }));
        return cachedUrl;
      }

      const imageUrl = getCustomPageImageContent(
        benguideId,
        customPageId,
        customPageSectionId,
        original,
        revision
      );

      // Fetch the actual image data and convert to base64 (following home page pattern)
      const base64ImageData = await getBase64Image(imageUrl, false, true);

      dispatch(
        getCustomPageImageContentSuccess({
          key,
          url: base64ImageData as string,
        })
      );
      return base64ImageData as string;
    } catch (error) {
      dispatch(getCustomPageImageContentFailed(error));
      throw error;
    }
  };

// Bulk fetch all image content for a custom page (following home page pattern)
export const handleGetAllCustomPageImageContent =
  (benguideId: string, customPageId: string, revision?: number) =>
  async (dispatch: any, getState: any): Promise<void> => {
    try {
      const state = getState();
      const customPages = state.customPages.customPages;

      // Find the current page
      const currentPage = customPages.find(
        (page: CustomPage) => page.id === customPageId
      );
      if (!currentPage || !currentPage.components) {
        return;
      }

      // Filter image components that have imageReference
      const imageComponents = currentPage.components.filter(
        (component: any) =>
          component.type === 'image' && component.imageReference
      );

      if (imageComponents.length === 0) {
        return;
      }

      // Only fetch images that are not already cached (following home page pattern)
      const fetchPromises: Promise<any>[] = [];

      imageComponents.forEach((component: any) => {
        // Check if cropped image is cached
        const croppedCached = isImageUrlCached(
          state,
          customPageId,
          component.id,
          false,
          revision
        );

        // Check if original image is cached
        const originalCached = isImageUrlCached(
          state,
          customPageId,
          component.id,
          true,
          revision
        );

        // Fetch cropped image if not cached
        if (!croppedCached) {
          fetchPromises.push(
            dispatch(
              handleGetCustomPageImageContent(
                benguideId,
                customPageId,
                component.id!,
                false,
                revision
              )
            )
          );
        }

        // Fetch original image if not cached and imageReference exists
        if (!originalCached && component.imageReference) {
          fetchPromises.push(
            dispatch(
              handleGetCustomPageImageContent(
                benguideId,
                customPageId,
                component.id!,
                true,
                revision
              )
            )
          );
        }
      });

      // Wait for all image fetches to complete
      if (fetchPromises.length > 0) {
        await Promise.allSettled(fetchPromises);
      }
    } catch (error) {
      // Don't throw error to prevent breaking the page load
      console.warn('Error fetching custom page images:', error);
    }
  };

// Update custom page rich media content action
export const updateCustomPageRichMediaContentAction =
  (
    benguideId: string,
    customPageId: string,
    customPageSectionId: string,
    content: string,
    title?: string,
    mediaType?: 'RICH_MEDIA' | 'CONTENT_CARD',
    callback?: Function
  ) =>
  async (dispatch: any, getState: any): Promise<void> => {
    dispatch(richMediaUpdateStarted());

    try {
      const requestData: CustomPageRichMediaContentVO = {
        customPageId,
        customPageSectionId,
        content,
        title,
        mediaType,
      };

      const response = await updateCustomPageRichMediaContent(
        benguideId,
        requestData
      );

      // Get current state to preserve existing image cache entries
      const currentState = getState();
      const oldRevision =
        currentState.benguide?.latestRevision ||
        currentState.benguide?.revision;
      const newRevision = response.latestRevision;

      // Update the latest revision properly with the value from API response
      if (response.latestRevision) {
        dispatch(updateBenguideLatestRevision(response.latestRevision));
      }

      // Preserve existing image cache entries by copying them with the new revision
      // This prevents image components from losing their cached URLs when rich media updates
      if (oldRevision && newRevision && oldRevision !== newRevision) {
        const existingImageUrls = currentState.customPages.imageContentUrls;
        const updatedImageUrls: Record<string, string> = {};

        Object.keys(existingImageUrls).forEach((key) => {
          // Only copy keys that belong to the same custom page and have the old revision
          if (
            key.includes(`${customPageId}-`) &&
            key.endsWith(`-${oldRevision}`)
          ) {
            const newKey = key.replace(`-${oldRevision}`, `-${newRevision}`);
            updatedImageUrls[newKey] = existingImageUrls[key];
          }
        });

        // Batch update all image URLs at once to prevent race conditions
        if (Object.keys(updatedImageUrls).length > 0) {
          Object.entries(updatedImageUrls).forEach(([key, url]) => {
            dispatch(
              getCustomPageImageContentSuccess({
                key,
                url,
              })
            );
          });
        }
      }

      // Update the richMediaContent cache immediately (following home page pattern)
      dispatch(
        richMediaFetchSuccess({
          customPageId,
          componentId: customPageSectionId,
          content,
          title: title || '',
        })
      );

      dispatch(
        richMediaUpdateSuccess({
          customPageId,
          componentId: customPageSectionId,
          content,
          title,
        })
      );

      dispatch(richMediaUpdateCompleted());

      // After successful content update, ensure image cache is consistent
      // This helps prevent showing stale images when content is updated after image updates
      const updatedState = getState();
      const updatedPage = updatedState.customPages.customPages.find(
        (page: CustomPage) => page.id === customPageId
      );

      if (updatedPage && updatedPage.components) {
        const imageComponents = updatedPage.components.filter(
          (component: any) =>
            component.type === 'image' && component.imageReference
        );

        // Log cache status for debugging
        imageComponents.forEach((component: any) => {
          const currentRevision =
            updatedPage.latestRevision || updatedPage.revision;
          const croppedKey = `${customPageId}-${component.id}-cropped-${currentRevision}`;

          if (!updatedState.customPages.imageContentUrls[croppedKey]) {
            console.log('🔄 Image cache miss after content update:', {
              componentId: component.id,
              customPageId,
              revision: currentRevision,
              missingKey: croppedKey,
            });
          }
        });
      }

      callback && callback();
    } catch (error) {
      dispatch(richMediaUpdateFailed(error));
      throw error;
    }
  };

// Get custom page rich media content action
export const getCustomPageRichMediaContentAction =
  (
    benguideId: string,
    customPageId: string,
    customPageSectionId: string,
    callback?: Function
  ) =>
  async (dispatch: any, getState: any): Promise<void> => {
    dispatch(richMediaFetchStarted());

    try {
      // Check if already cached first (following home page pattern)
      const state = getState();
      const key = `${customPageId}-${customPageSectionId}`;
      const cachedContent = state.customPages.richMediaContent[key];

      if (cachedContent) {
        dispatch(
          richMediaFetchSuccess({
            customPageId,
            componentId: customPageSectionId,
            content: cachedContent.content,
            title: cachedContent.title,
          })
        );
        callback && callback();
        return;
      }

      const response = await getCustomPageRichMediaContent(
        benguideId,
        customPageId,
        customPageSectionId
      );

      dispatch(
        richMediaFetchSuccess({
          customPageId,
          componentId: customPageSectionId,
          content: response.content,
          title: response.title,
        })
      );

      callback && callback();
    } catch (error) {
      dispatch(richMediaFetchFailed(error));
      throw error;
    }
  };

// API-based section reordering actions
export const moveSectionUpWithAPI =
  (benguideId: string, customPageId: string, sectionIndex: number) =>
  async (dispatch: any, getState: any): Promise<void> => {
    try {
      const state = getState();
      const page = state.customPages.customPages.find(
        (p: CustomPage) => p.id === customPageId
      );

      if (
        !page ||
        sectionIndex <= 0 ||
        sectionIndex >= page.components.length
      ) {
        return;
      }

      const currentComponent = page.components[sectionIndex];
      const newOrderNo = sectionIndex; // Moving up means decreasing order number (1-based)

      dispatch(updateSectionOrderStarted());

      // First update local state for immediate UI feedback
      dispatch(moveSectionUp({ pageId: customPageId, sectionIndex }));

      // Then call the API
      await updateCustomPageSectionOrder(
        benguideId,
        customPageId,
        currentComponent.id,
        newOrderNo
      );

      dispatch(updateSectionOrderSuccess());
    } catch (error) {
      // Revert local state on API failure by moving section back down
      dispatch(
        moveSectionDown({
          pageId: customPageId,
          sectionIndex: sectionIndex - 1,
        })
      );
      dispatch(updateSectionOrderFailed(error));
      throw error;
    }
  };

export const moveSectionDownWithAPI =
  (benguideId: string, customPageId: string, sectionIndex: number) =>
  async (dispatch: any, getState: any): Promise<void> => {
    try {
      const state = getState();
      const page = state.customPages.customPages.find(
        (p: CustomPage) => p.id === customPageId
      );

      if (
        !page ||
        sectionIndex < 0 ||
        sectionIndex >= page.components.length - 1
      ) {
        return;
      }

      const currentComponent = page.components[sectionIndex];
      const newOrderNo = sectionIndex + 2; // Moving down means increasing order number (1-based)

      dispatch(updateSectionOrderStarted());

      // First update local state for immediate UI feedback
      dispatch(moveSectionDown({ pageId: customPageId, sectionIndex }));

      // Then call the API
      await updateCustomPageSectionOrder(
        benguideId,
        customPageId,
        currentComponent.id,
        newOrderNo
      );

      dispatch(updateSectionOrderSuccess());
    } catch (error) {
      dispatch(
        moveSectionUp({ pageId: customPageId, sectionIndex: sectionIndex + 1 })
      );
      dispatch(updateSectionOrderFailed(error));
      throw error;
    }
  };
